'use client'

import { ReactNode, useState, useMemo, useEffect, useRef } from 'react'

import { Session } from 'next-auth'
import qs from 'qs'

import {
  QueryClient,
  QueryClientProvider,
  DefaultOptions,
  QueryKey,
  IsRestoringProvider
} from '@tanstack/react-query'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'

import { useDataStore } from '@/shared/lib/dataStore'
import { createBootstrapPersister } from '@/shared/lib/persistence'
import { api } from '@/shared/services/api'

const MAX_AGE_24H = 1000 * 60 * 60 * 24

const defaultQueryFn = async ({ queryKey }: { queryKey: QueryKey }) => {
  const [resource, maybeIdOrParams, maybeParams] = queryKey

  if (typeof resource !== 'string') {
    throw new Error(
      'Invalid queryKey: first item must be a string (resource path)'
    )
  }

  let url = resource
  let params: Record<string, unknown> = {}

  if (
    typeof maybeIdOrParams === 'number' ||
    typeof maybeIdOrParams === 'string'
  ) {
    url += `/${maybeIdOrParams}`
    if (maybeParams && typeof maybeParams === 'object') {
      params = maybeParams as Record<string, unknown>
    }
  } else if (maybeIdOrParams && typeof maybeIdOrParams === 'object') {
    params = maybeIdOrParams as Record<string, unknown>
  }

  const queryString = qs.stringify(params, {
    arrayFormat: 'brackets'
  })
  const fullUrl = queryString ? `${url}?${queryString}` : url

  const { data } = await api.get(fullUrl)
  return data
}

function shouldDehydrateQuery(query: {
  queryKey: readonly unknown[]
}): boolean {
  const key = query.queryKey[0]
  if (key === 'bootstrap') return true
  if (typeof key !== 'string') return false
  return (
    key === '/user/gamification/progress' ||
    key === '/user/profiles' ||
    key === '/user/missions/progresses' ||
    key === '/user/journeys' ||
    key.startsWith('/missions') ||
    key.startsWith('/journeys')
  )
}

type QueryProviderProps = {
  session: Session | null
  children: ReactNode
}

export function QueryProvider({ session, children }: QueryProviderProps) {
  const gamificationVersion = useDataStore(s => s.data.version)
  const defaultOptions: DefaultOptions = {
    queries: {
      refetchOnReconnect: 'always',
      refetchOnWindowFocus: false,
      refetchInterval: 0,
      retry: (failureCount) => failureCount < 2,
      retryDelay: attempt => Math.min(500 * 2 ** attempt, 2000),
      refetchOnMount: false,
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 30,
      queryFn: defaultQueryFn
    }
  }
  const [queryClient] = useState(() => new QueryClient({ defaultOptions }))
  const previousVersionRef = useRef<string | null>(null)

  useEffect(() => {
    const previousVersion = previousVersionRef.current
    previousVersionRef.current = gamificationVersion ?? null

    if (
      !session?.user?.id ||
      !previousVersion ||
      !gamificationVersion ||
      previousVersion === gamificationVersion
    ) {
      return
    }

    queryClient.removeQueries({
      predicate: query => {
        const key = query.queryKey[0]
        return (
          key === '/user/journeys' ||
          key === '/user/passes' ||
          (typeof key === 'string' && key.startsWith('/journeys'))
        )
      }
    })

    if (typeof window !== 'undefined') {
      const userKeyPrefix = `pgee-cache-${session.user.id}-`
      const keys = Object.keys(window.localStorage)
      keys.forEach(key => {
        if (key.startsWith(userKeyPrefix)) {
          window.localStorage.removeItem(key)
        }
      })
    }
  }, [gamificationVersion, queryClient, session?.user?.id])

  const persistOptions = useMemo(() => {
    if (typeof window === 'undefined' || !session?.user?.id) return null
    return {
      persister: createBootstrapPersister(
        session.user.id as string,
        gamificationVersion
      ),
      maxAge: MAX_AGE_24H,
      dehydrateOptions: {
        shouldDehydrateQuery: shouldDehydrateQuery as (q: unknown) => boolean
      }
    }
  }, [session?.user?.id, gamificationVersion])

  if (persistOptions) {
    return (
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={persistOptions}
      >
        {children}
      </PersistQueryClientProvider>
    )
  }

  return (
    <QueryClientProvider client={queryClient}>
      <IsRestoringProvider value={false}>{children}</IsRestoringProvider>
    </QueryClientProvider>
  )
}
